import { Link } from 'react-router-dom';
import type { Post } from '@/mocks/posts';

type Props = {
  featured: Post;
  secondary: Post[];
};

export default function Hero({ featured, secondary }: Props) {
  return (
    <section className="w-full px-4 pb-10 pt-8 md:px-8" aria-label="今日头条">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <article className="reveal-up lg:col-span-2">
          <Link
            to={`/post/${featured.id}`}
            className="group relative block h-[320px] w-full overflow-hidden rounded-lg md:h-[420px]"
          >
            <img
              src={featured.cover}
              alt={featured.coverAlt}
              className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10"></div>
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
              <div className="mb-3 flex items-center gap-2">
                <span className="flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                  <i className="ri-fire-fill" aria-hidden="true"></i>
                  今日头条
                </span>
                <span className="rounded-full bg-background-50/20 px-3 py-1 text-xs font-medium text-background-50 backdrop-blur">
                  {featured.categoryLabel}
                </span>
              </div>
              <h2 className="font-heading text-2xl font-bold leading-snug text-white md:text-4xl">
                {featured.title}
              </h2>
              <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-white/80 md:block">
                {featured.summary}
              </p>
              <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-white/70">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line" aria-hidden="true"></i>
                  {featured.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" aria-hidden="true"></i>
                  {featured.views.toLocaleString('zh-CN')} 次围观
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {featured.readMinutes} 分钟读完
                </span>
              </div>
            </div>
          </Link>
        </article>

        <div className="flex flex-col gap-5">
          {secondary.map((post, index) => (
            <article key={post.id} className="reveal-up flex-1" style={{ animationDelay: `${index * 90}ms` }}>
              <Link
                to={`/post/${post.id}`}
                className="group flex h-full flex-col justify-between rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
              >
                <div>
                  <div className="mb-2 flex items-center gap-2">
                    <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs font-medium text-secondary-900">
                      {post.categoryLabel}
                    </span>
                    {post.hot && (
                      <span className="flex items-center gap-1 text-xs font-medium text-primary-600">
                        <i className="ri-fire-fill" aria-hidden="true"></i>
                        热
                      </span>
                    )}
                  </div>
                  <h3 className="font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                    {post.title}
                  </h3>
                  <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-foreground-600">{post.summary}</p>
                </div>
                <div className="mt-3 flex items-center gap-3 text-xs text-foreground-500">
                  <span>{post.publishedAt.slice(5, 16)}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {post.views.toLocaleString('zh-CN')}
                  </span>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}